import { z } from 'zod';
import { uuidV7Schema } from './ids';
import {
  modelCredentialsSchema,
  modelCredentialModeSchema,
} from './model-credentials';

export const companyRoleSchema = z.enum(['owner', 'admin', 'member']);
export type CompanyRole = z.infer<typeof companyRoleSchema>;
export const accountMembershipSchema = z
  .object({
    companyId: uuidV7Schema,
    name: z.string().min(1),
    role: companyRoleSchema,
  })
  .strict();
export const credentialProfileSchema = z
  .object({
    id: uuidV7Schema,
    name: z.string().min(1),
    openaiConfigured: z.boolean(),
    jevConfigured: z.boolean(),
    anthropicConfigured: z.boolean(),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .strict();
export const saveCredentialProfileSchema = z
  .object({
    id: uuidV7Schema.optional(),
    name: z.string().trim().min(1).max(100),
    encryptionBotId: uuidV7Schema.optional(),
    openaiKey: modelCredentialsSchema.shape.openaiKey.optional(),
    jevKey: modelCredentialsSchema.shape.jevKey.optional(),
    anthropicKey: modelCredentialsSchema.shape.anthropicKey,
  })
  .strict();
export const credentialAssignmentSchema = z
  .object({
    botId: uuidV7Schema,
    mode: modelCredentialModeSchema,
    profileId: uuidV7Schema.nullable(),
  })
  .strict()
  .refine(
    (value) => value.mode !== 'bot_required' || value.profileId !== null,
    {
      message: 'Own credentials require an account credential profile.',
      path: ['profileId'],
    }
  );
export const accountCredentialsSchema = z
  .object({
    profiles: z.array(credentialProfileSchema),
    assignments: z.array(
      z
        .object({
          botId: uuidV7Schema,
          name: z.string(),
          mode: modelCredentialModeSchema,
          profileId: uuidV7Schema.nullable(),
        })
        .strict()
    ),
  })
  .strict();

export const operatorRoleSchema = z.enum([
  'owner',
  'admin',
  'operator',
  'viewer',
]);
export const accountBotGrantSchema = z
  .object({ botId: uuidV7Schema, role: operatorRoleSchema })
  .strict();
export const accountInvitationInputSchema = z
  .object({
    id: uuidV7Schema,
    email: z.email().toLowerCase(),
    role: companyRoleSchema,
    adviserId: uuidV7Schema.nullable(),
    botGrants: z
      .array(accountBotGrantSchema)
      .max(100)
      .refine(
        (values) =>
          new Set(values.map((value) => value.botId)).size === values.length
      ),
    senderBotId: uuidV7Schema,
    senderConnectionId: uuidV7Schema,
    locale: z.enum(['es', 'en']),
  })
  .strict();
export type AccountInvitationInput = z.infer<
  typeof accountInvitationInputSchema
>;
export const invitationStatusSchema = z.enum([
  'queued',
  'preparing',
  'prepared',
  'sending',
  'sent',
  'uncertain',
  'failed',
  'accepted',
  'revoked',
]);
export const accountInvitationSchema = z
  .object({
    id: uuidV7Schema,
    email: z.email(),
    role: companyRoleSchema,
    adviserId: uuidV7Schema.nullable(),
    status: invitationStatusSchema,
    expiresAt: z.iso.datetime({ offset: true }),
    error: z.string().nullable(),
  })
  .strict();
export const accountMemberSchema = z
  .object({
    id: uuidV7Schema,
    name: z.string(),
    email: z.email().nullable(),
    provider: z.enum(['google', 'microsoft']),
    role: companyRoleSchema.nullable(),
    adviserId: uuidV7Schema.nullable(),
    botGrants: z.array(accountBotGrantSchema),
  })
  .strict();
export const accountAccessSchema = z
  .object({
    members: z.array(accountMemberSchema),
    invitations: z.array(accountInvitationSchema),
    nextCursor: uuidV7Schema.nullable(),
  })
  .strict();
export const updateAccountMemberSchema = z
  .object({
    accountId: uuidV7Schema,
    role: companyRoleSchema.nullable(),
    adviserId: uuidV7Schema.nullable(),
    botGrants: z
      .array(accountBotGrantSchema)
      .max(100)
      .refine(
        (values) =>
          new Set(values.map((value) => value.botId)).size === values.length
      ),
  })
  .strict();
